import React, { useMemo } from "react";
import { Show, InfoBox } from "@9amhealth/shared";
import type { ObservationFulfillmentItem } from "@9amhealth/openapi";
import styled from "@emotion/styled";
import { IconCheckmarkRoundGreenBackground } from "src/constants/icons";
import LabListRow from "src/ui/components/UserLabResults/LabListRow";
import translate from "src/lib/translate";
import ListToText from "../ListToText/ListToText";
import CollectionTitle from "../StyledComponents/CollectionTitle";

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  padding-bottom: 6rem;
  text-align: left;
`;

const Card = styled.div`
  background: var(--color-white);
  border-radius: 0.8rem;
  overflow: hidden;
  box-shadow: var(--light-shadow);
`;

interface InitialLabsListProps {
  allLabs: ObservationFulfillmentItem[];
}

const InitialLabsList: React.FC<InitialLabsListProps> = ({ allLabs }) => {
  const incompleteRequiredLabs = useMemo(
    () =>
      allLabs.filter((lab) => lab.status === "INCOMPLETE" && lab.isRequired),
    [allLabs]
  );
  const completedLabs = useMemo(
    () => allLabs.filter((lab) => lab.status !== "INCOMPLETE"),
    [allLabs]
  );

  const incompleteRequiredLabNames = incompleteRequiredLabs.map(
    (lab) => lab.displayName
  );

  const partialDescriptionText = translate(
    "task.yourInitialLab.partialDescription.fewResultsMissing"
  );

  return (
    <Container>
      <Show when={incompleteRequiredLabs.length > 0}>
        <InfoBox
          title={translate("task.yourInitialLab.title_fewResultsMissing")}
        >
          {partialDescriptionText}{" "}
          <ListToText list={incompleteRequiredLabNames} bold />.
        </InfoBox>
      </Show>

      <Show when={completedLabs.length > 0}>
        <div style={{ height: "1.5rem" }} />
        <CollectionTitle>
          {translate("labs.imported", { count: completedLabs.length })}
        </CollectionTitle>
        <Card>
          {completedLabs.map((lab) => (
            <LabListRow
              key={lab.type}
              label={lab.displayName}
              icon={<IconCheckmarkRoundGreenBackground />}
              secondaryText="Imported"
            />
          ))}
        </Card>
      </Show>
    </Container>
  );
};

export default InitialLabsList;
